/**
 * How much of a garment is not there.
 *
 * Transparency is a technique in Roblox clothing, not a mistake — a transparent
 * sleeve region is how you get short sleeves. So this measures rather than
 * warns: it says what will show skin, and the person deciding whether that is
 * right is the person who drew it.
 *
 * **Measured inside the face regions only.** 54.9% of the 585×559 template
 * never renders on the avatar — the gutters and the dead space around the net —
 * so a ratio over the whole canvas would report every design as mostly
 * transparent and mean nothing. The denominator is the pixels Roblox actually
 * samples.
 *
 * Pure, and in `shared/` because the export step and any future preflight both
 * want it and neither should own it.
 */
import type { Bitmap } from "./edge";
import { REGION_IDS, type RegionId } from "./format";
import { REGIONS } from "./regions";

/**
 * Below this alpha, a pixel shows body.
 *
 * Not zero. Anti-aliased edges and a soft brush leave a fringe of alpha 1–7
 * that is invisible against skin, and counting those as covered would report a
 * cut-out as solid. Roblox composites with the same indifference to whether a
 * nearly-transparent pixel was deliberate.
 */
const BARE_ALPHA = 8;

export interface RegionCoverage {
  id: RegionId;
  pixels: number;
  bare: number;
  /** 0..1 within this region. */
  bareRatio: number;
}

export interface Coverage {
  /** Pixels Roblox samples. The denominator, and never the canvas area. */
  regionPixels: number;
  /** Of those, how many will show body. */
  barePixels: number;
  /** 0..1 across the garment. */
  bareRatio: number;
  /** Every region, so a caller can name the ones that matter. */
  regions: RegionCoverage[];
  /**
   * Regions with nothing in them at all.
   *
   * The actionable half. "38% shows skin" is a number; "your left arm is empty"
   * is something a person can act on, and it is the difference between a
   * deliberate short sleeve and a layer somebody forgot to make visible.
   */
  emptyRegions: RegionId[];
}

/**
 * Measure a composited garment.
 *
 * `bitmap` is the flattened artwork — what the export pipeline runs on and what
 * Roblox would receive, before edge treatment. Edge work only writes into the
 * gutters and under transparent pixels, so it cannot change this answer.
 */
export function coverageOf(bitmap: Bitmap, bareAlpha: number = BARE_ALPHA): Coverage {
  const regions: RegionCoverage[] = [];
  const emptyRegions: RegionId[] = [];
  let regionPixels = 0;
  let barePixels = 0;

  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    // A region running off the bitmap is a bitmap of the wrong size, which the
    // export path already refuses — clamped here so this can never read outside
    // its own buffer even if that check is ever moved.
    const x1 = Math.min(r.x + r.w, bitmap.width);
    const y1 = Math.min(r.y + r.h, bitmap.height);

    let pixels = 0;
    let bare = 0;
    for (let y = Math.max(0, r.y); y < y1; y++) {
      // The alpha byte of the first pixel in this row of this region.
      let i = (y * bitmap.width + Math.max(0, r.x)) * 4 + 3;
      for (let x = Math.max(0, r.x); x < x1; x++, i += 4) {
        pixels++;
        if ((bitmap.data[i] ?? 0) < bareAlpha) bare++;
      }
    }

    regionPixels += pixels;
    barePixels += bare;
    regions.push({ id, pixels, bare, bareRatio: pixels === 0 ? 0 : bare / pixels });
    // `pixels > 0` so a zero-area region is not reported as an empty limb.
    if (pixels > 0 && bare === pixels) emptyRegions.push(id);
  }

  return {
    regionPixels,
    barePixels,
    bareRatio: regionPixels === 0 ? 0 : barePixels / regionPixels,
    regions,
    emptyRegions,
  };
}

/**
 * Nothing has been painted at all.
 *
 * Distinct from "a lot of skin shows", which is what a short-sleeved shirt
 * looks like and is not worth a word. This is the case where somebody is one
 * click from spending 80 Robux and a moderation queue on an empty PNG.
 *
 * Guards on `regions.length` so a coverage with no regions measured — which is
 * what a zero-size or unreadable bitmap produces — reports *not* blank. A
 * measurement that failed must not accuse the user of drawing nothing.
 */
export function isBlank(c: Coverage): boolean {
  return c.regions.length > 0 && c.emptyRegions.length === c.regions.length;
}
